import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Image as ImageIcon, Crop, Plus, X, Link as LinkIcon, Zap, GripVertical, MousePointerClick, MessageCircle, Shuffle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { updateNode, createButton, updateButton, deleteButton, reorderButtons } from '@/services/api';
import { getPageVariables } from '@/services/api/connections';
import { useQuery } from '@tanstack/react-query';
import type { FlowNode } from '@/types/flow';
import type { MediaCrop, CropMediaResponse } from '@/types/media';
import MediaLibraryModal from '../MediaLibraryModal';
import ImageCropModal from '../ImageCropModal';
import { versaoLeve, aoFalharVersaoLeve } from '@/lib/mediaLeve';
import SmartTextField from '@/pages/Meta/Broadcast/components/SmartTextField';
import type { PageVariable } from '@/pages/Meta/Connection/types/meta.types';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface PhotoTemplateConfigProps {
    node: FlowNode;
    onClose: () => void;
    pageId: string;
}

interface ButtonItem {
    id: number | null;
    tempId: string;
    text: string;
    type: 'url' | 'postback';
    url?: string;
    url_entries?: { url: string; weight: number }[];
    payload?: string;
    isNew?: boolean;
    isDeleted?: boolean;
    isModified?: boolean;
}

const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const getButtonUniqueId = (button: ButtonItem): string | number => {
    return button.id !== null ? button.id : button.tempId;
};

function SortableButton({
    button,
    index,
    onUpdate,
    onRemove,
    disabled,
    pageVariables,
}: {
    button: ButtonItem;
    index: number;
    onUpdate: (tempId: string, field: keyof ButtonItem, value: any) => void;
    onRemove: (tempId: string) => void;
    disabled?: boolean;
    pageVariables: PageVariable[];
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: getButtonUniqueId(button) });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`p-3 bg-white border rounded-lg space-y-2 ${isDragging ? 'border-cyan-400 shadow-lg' : 'border-gray-200'
                }`}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <div
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded p-0.5"
                    >
                        <GripVertical className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                    <span className="text-xs font-medium text-gray-700">
                        Botão {index + 1}
                        {button.isNew && <span className="ml-1.5 text-[10px] text-green-600">(novo)</span>}
                    </span>
                </div>
                <button
                    onClick={() => onRemove(button.tempId)}
                    disabled={disabled}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors disabled:opacity-50"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                    <Label className="text-[10px] text-gray-600">Texto</Label>
                    <Input
                        value={button.text}
                        onChange={(e) => onUpdate(button.tempId, 'text', e.target.value)}
                        placeholder="Ex: Ver mais"
                        className="h-8 text-xs"
                        maxLength={20}
                        disabled={disabled}
                    />
                </div>

                <div className="space-y-1">
                    <Label className="text-[10px] text-gray-600">Tipo</Label>
                    <Select
                        value={button.type}
                        onValueChange={(value: 'url' | 'postback') => {
                            onUpdate(button.tempId, 'type', value);
                            if (value === 'postback') {
                                onUpdate(button.tempId, 'url', undefined);
                                onUpdate(button.tempId, 'url_entries', undefined);
                            } else {
                                onUpdate(button.tempId, 'url_entries', [{ url: '', weight: 100 }]);
                            }
                        }}
                        disabled={disabled}
                    >
                        <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="postback">
                                <div className="flex items-center gap-1.5">
                                    <Zap className="w-3 h-3" />
                                    <span className="text-xs">Ação</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="url">
                                <div className="flex items-center gap-1.5">
                                    <LinkIcon className="w-3 h-3" />
                                    <span className="text-xs">Link</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {button.type === 'url' && (() => {
                const entries = button.url_entries || [];
                const totalWeight = entries.reduce((acc, curr) => acc + (curr.weight || 0), 0);
                const weightOk = Math.round(totalWeight) === 100;

                return (
                    <div className="space-y-3 mt-4 border-t pt-4">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                    <Shuffle className="w-3.5 h-3.5 text-blue-500" />
                                    <span className="text-xs font-semibold text-gray-600">URLs com peso (random)</span>
                                </div>
                                {entries.length > 0 && (
                                    <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded-full ${weightOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                        {totalWeight}%
                                    </span>
                                )}
                            </div>

                            {entries.map((entry, entryIdx) => (
                                <div key={entryIdx} className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 space-y-1.5 relative group">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-blue-700">URL {entryIdx + 1}</span>
                                        {entries.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newEntries = [...entries];
                                                    newEntries.splice(entryIdx, 1);
                                                    onUpdate(button.tempId, 'url_entries', newEntries);
                                                }}
                                                className="p-0.5 text-blue-400 hover:text-red-500"
                                                disabled={disabled}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                    <SmartTextField
                                        value={entry.url}
                                        onChange={(val) => {
                                            const newEntries = [...entries];
                                            newEntries[entryIdx] = { ...newEntries[entryIdx], url: val };
                                            onUpdate(button.tempId, 'url_entries', newEntries);
                                        }}
                                        variables={pageVariables}
                                        placeholder="https://exemplo.com/{{variavel}}"
                                        singleLine
                                        inputClassName="h-7 text-xs"
                                    />
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-xs text-blue-600 whitespace-nowrap">Peso (%)</span>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            placeholder="0"
                                            value={entry.weight === 0 && entry.url === '' ? '' : entry.weight}
                                            disabled={disabled}
                                            onChange={(e) => {
                                                const newEntries = [...entries];
                                                newEntries[entryIdx] = { ...newEntries[entryIdx], weight: Number(e.target.value) || 0 };
                                                onUpdate(button.tempId, 'url_entries', newEntries);
                                            }}
                                            className="h-7 text-xs w-20 bg-white"
                                        />
                                    </div>
                                </div>
                            ))}

                            {entries.length > 0 && !weightOk && (
                                <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span>A soma dos pesos deve ser 100%. Atual: <strong>{totalWeight}%</strong></span>
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={() => {
                                    onUpdate(button.tempId, 'url_entries', [
                                        ...entries,
                                        { url: '', weight: entries.length === 0 ? 100 : 0 }
                                    ]);
                                }}
                                disabled={disabled || entries.length >= 5}
                                className="w-full py-1.5 text-xs border border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-blue-500 hover:text-blue-700 flex items-center justify-center gap-1 mt-2 disabled:opacity-50"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Adicionar URL
                            </button>
                        </div>
                    </div>
                );
            })()}

            <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                {button.type === 'url' ? (
                    <>
                        <LinkIcon className="w-3 h-3" />
                        <span>Abrirá link externo</span>
                    </>
                ) : (
                    <>
                        <Zap className="w-3 h-3" />
                        <span>Continuará no fluxo</span>
                    </>
                )}
            </div>
        </div>
    );
}

export default function PhotoTemplateConfig({ node, onClose, pageId }: PhotoTemplateConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [title, setTitle] = useState(node.data.config?.title || '');
    const [subtitle, setSubtitle] = useState(node.data.config?.subtitle || '');
    const [mediaUrl, setMediaUrl] = useState(node.data.config?.media_url || '');
    const [mediaFilename, setMediaFilename] = useState(node.data.config?.media_filename || '');
    const [typingSeconds, setTypingSeconds] = useState(node.data.config?.delay_seconds || 0);
    const [buttons, setButtons] = useState<ButtonItem[]>([]);
    const [originalButtons, setOriginalButtons] = useState<ButtonItem[]>([]);
    const [mediaClickUrlEntries, setMediaClickUrlEntries] = useState<{ url: string; weight: number }[]>([]);

    // A imagem enviada e a versao JA RECORTADA. As duas abaixo existem so para o
    // editor conseguir reabrir no mesmo enquadramento: sem elas, reajustar
    // significaria escolher a arte de novo e refazer o corte no olho.
    const [mediaSourceUrl, setMediaSourceUrl] = useState(node.data.config?.media_source_url || '');
    const [mediaCrop, setMediaCrop] = useState<MediaCrop | null>(node.data.config?.media_crop || null);

    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
    const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);

    const { data: pageVariables = [] } = useQuery({
        queryKey: ['page-variables', pageId],
        queryFn: () => getPageVariables(Number(pageId)),
        enabled: !!pageId,
    });

    useEffect(() => {
        if (buttons.length > 0 && !node.data.id) return;

        const initialButtons: ButtonItem[] = (node.data.buttons || []).map((b) => {
            const btn = b as any;
            return {
                id: btn.id,
                tempId: btn.id ? `db-${btn.id}` : generateTempId(),
                text: btn.button_text,
                type: btn.button_type || 'postback',
                url: btn.button_type === 'url' ? btn.button_value : undefined,
                url_entries: btn.button_type === 'url' && btn.url_entries && btn.url_entries.length > 0
                    ? btn.url_entries
                    : (btn.button_type === 'url' ? [{ url: btn.button_value || '', weight: 100 }] : undefined),
                payload: btn.button_type === 'postback' ? btn.button_value : undefined,
                isNew: false,
                isDeleted: false,
                isModified: false,
            };
        });
        setButtons(initialButtons);
        setOriginalButtons(JSON.parse(JSON.stringify(initialButtons)));

        // Inicializa mediaClickUrlEntries do node.message (que pode ser JSON ou string simples)
        const rawMessage = node.data.config?.message || '';
        let initialMediaEntries: { url: string; weight: number }[] = [];

        if (rawMessage.trim().startsWith('[') || rawMessage.trim().startsWith('{')) {
            try {
                const parsed = JSON.parse(rawMessage);
                if (Array.isArray(parsed)) {
                    initialMediaEntries = parsed;
                } else {
                    initialMediaEntries = [{ url: rawMessage, weight: 100 }];
                }
            } catch {
                initialMediaEntries = [{ url: rawMessage, weight: 100 }];
            }
        } else if (rawMessage) {
            initialMediaEntries = [{ url: rawMessage, weight: 100 }];
        }

        setMediaClickUrlEntries(initialMediaEntries);
    }, [node.data.buttons, node.data.config?.message]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setButtons((items) => {
                const oldIndex = items.findIndex((item) => getButtonUniqueId(item) === active.id);
                const newIndex = items.findIndex((item) => getButtonUniqueId(item) === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const updateNodeMutation = useMutation({
        mutationFn: () => {
            const mediaClickUrlValue = mediaClickUrlEntries.length > 1
                ? JSON.stringify(mediaClickUrlEntries)
                : (mediaClickUrlEntries[0]?.url || '');

            return updateNode(node.data.id, {
                node_name: nodeName,
                config: {
                    title,
                    subtitle,
                    media_url: mediaUrl,
                    media_filename: mediaFilename,
                    media_type: 'image',
                    media_source_url: mediaSourceUrl || null,
                    media_crop: mediaCrop,
                    message: mediaClickUrlValue || undefined,
                    delay_seconds: typingSeconds,
                }
            });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const createButtonMutation = useMutation({
        mutationFn: (data: { button_text: string; button_type: 'url' | 'postback'; button_value: string; position: number; url_entries?: any[] }) =>
            createButton(node.data.id, data),
    });

    const updateButtonMutation = useMutation({
        mutationFn: ({ buttonId, data }: { buttonId: number; data: any }) =>
            updateButton(buttonId, data),
    });

    const deleteButtonMutation = useMutation({
        mutationFn: (buttonId: number) => deleteButton(buttonId),
    });

    const reorderButtonsMutation = useMutation({
        mutationFn: (buttonOrder: number[]) => reorderButtons(node.data.id, buttonOrder),
    });

    const handleSave = async () => {
        if (!title.trim()) {
            toast({
                variant: 'destructive',
                title: 'Título obrigatório',
                description: 'Por favor, insira um título para o template.',
            });
            return;
        }

        if (!mediaUrl) {
            toast({
                variant: 'destructive',
                title: 'Imagem obrigatória',
                description: 'Por favor, selecione uma imagem.',
            });
            return;
        }

        const invalidButton = buttons.find(btn => {
            if (!btn.text.trim()) return true;
            if (btn.type === 'url') {
                const entries = btn.url_entries || [];
                if (entries.length === 0) return true;
                const totalWeight = entries.reduce((acc, curr) => acc + (curr.weight || 0), 0);
                if (Math.round(totalWeight) !== 100) return true;
                if (entries.some(e => !e.url.trim())) return true;
            }
            return false;
        });

        if (invalidButton) {
            toast({
                variant: 'destructive',
                title: 'Botão inválido',
                description: 'Verifique os textos, URLs e pesos dos botões (soma deve ser 100%).',
            });
            return;
        }

        try {
            const currentMediaClickUrl = mediaClickUrlEntries.length > 1
                ? JSON.stringify(mediaClickUrlEntries)
                : (mediaClickUrlEntries[0]?.url || '');

            const nodeHasChanges =
                nodeName !== node.data.node_name ||
                title !== node.data.config?.title ||
                subtitle !== node.data.config?.subtitle ||
                mediaUrl !== node.data.config?.media_url ||
                mediaFilename !== node.data.config?.media_filename ||
                mediaSourceUrl !== (node.data.config?.media_source_url || '') ||
                JSON.stringify(mediaCrop) !== JSON.stringify(node.data.config?.media_crop || null) ||
                currentMediaClickUrl !== (node.data.config?.message || '') ||
                typingSeconds !== (node.data.config?.delay_seconds || 0);

            if (nodeHasChanges) {
                await updateNodeMutation.mutateAsync();
            }

            const deletedButtons = originalButtons.filter(
                orig => !buttons.find(btn => btn.tempId === orig.tempId)
            );
            for (const btn of deletedButtons) {
                if (btn.id) {
                    await deleteButtonMutation.mutateAsync(btn.id);
                }
            }

            const newButtons = buttons.filter(btn => btn.isNew || btn.id === null);
            const tempIdToRealId = new Map<string, number>();

            for (const btn of newButtons) {
                const isUrl = btn.type === 'url';
                const created = await createButtonMutation.mutateAsync({
                    button_text: btn.text,
                    button_type: btn.type,
                    button_value: isUrl ? (btn.url_entries ? btn.url_entries[0]?.url || '' : btn.url || '') : (btn.payload || btn.text.toLowerCase().replace(/\s+/g, '_')),
                    position: buttons.indexOf(btn),
                    ...(isUrl && btn.url_entries && btn.url_entries.length > 0 ? { url_entries: btn.url_entries } : {})
                });
                tempIdToRealId.set(btn.tempId, created.id);
            }

            const modifiedButtons = buttons.filter(btn => {
                if (!btn.id || btn.isNew) return false;
                const orig = originalButtons.find(o => o.tempId === btn.tempId);
                if (!orig) return false;
                return orig.text !== btn.text ||
                    orig.type !== btn.type ||
                    orig.url !== btn.url ||
                    orig.payload !== btn.payload ||
                    JSON.stringify(orig.url_entries) !== JSON.stringify(btn.url_entries);
            });

            for (const btn of modifiedButtons) {
                if (btn.id) {
                    const isUrl = btn.type === 'url';
                    await updateButtonMutation.mutateAsync({
                        buttonId: btn.id,
                        data: {
                            button_text: btn.text,
                            button_type: btn.type,
                            button_value: isUrl ? (btn.url_entries ? btn.url_entries[0]?.url || '' : btn.url || '') : btn.payload,
                            ...(isUrl && btn.url_entries && btn.url_entries.length > 0 ? { url_entries: btn.url_entries } : { url_entries: null })
                        }
                    });
                }
            }

            const finalButtonOrder: number[] = buttons
                .map((btn) => {
                    if (btn.id) return btn.id;
                    return tempIdToRealId.get(btn.tempId) || null;
                })
                .filter((id): id is number => id !== null);

            if (finalButtonOrder.length > 1) {
                const originalOrder = originalButtons
                    .filter(btn => btn.id)
                    .map(btn => btn.id) as number[];

                const orderChanged = finalButtonOrder.length !== originalOrder.length ||
                    finalButtonOrder.some((id, index) => id !== originalOrder[index]);

                if (orderChanged) {
                    await reorderButtonsMutation.mutateAsync(finalButtonOrder);
                }
            }

            toast({
                title: 'Configurações salvas!',
                description: 'O template foi atualizado com sucesso.',
            });

            await queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        } catch (error: any) {
            console.error('Erro ao salvar:', error);
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || error.message || 'Ocorreu um erro.',
            });
        }
    };

    // Escolher a arte nao basta: ela vai para o editor antes de virar a imagem do
    // node, porque e ali que o enquadramento 1,91:1 e decidido.
    const handleMediaSelect = (media: any) => {
        setIsMediaLibraryOpen(false);
        setMediaCrop(null);
        setCropSourceUrl(media.file_url);
    };

    const handleCropConfirm = (resultado: CropMediaResponse) => {
        setMediaUrl(resultado.file_url);
        setMediaFilename(resultado.filename);
        setMediaSourceUrl(resultado.source_url);
        setMediaCrop(resultado.crop);
    };

    const handleAddButton = () => {
        if (buttons.length >= 3) {
            toast({
                variant: 'destructive',
                title: 'Limite atingido',
                description: 'Máximo de 3 botões permitidos.',
            });
            return;
        }

        const newButton: ButtonItem = {
            id: null,
            tempId: generateTempId(),
            text: '',
            type: 'postback',
            isNew: true,
        };

        setButtons([...buttons, newButton]);
    };

    // Botão já salvo: o histórico de cliques é por id, e o id morre com ele.
    const [confirmRemove, setConfirmRemove] = useState<ButtonItem | null>(null);
    const handleRemoveButton = (tempId: string) => {
        const btn = buttons.find(b => b.tempId === tempId);
        if (btn && !btn.isNew && btn.id) {
            setConfirmRemove(btn);
            return;
        }
        setButtons(buttons.filter(b => b.tempId !== tempId));
    };

    const handleUpdateButton = (tempId: string, field: keyof ButtonItem, value: any) => {
        setButtons(prev => prev.map(btn =>
            btn.tempId === tempId ? { ...btn, [field]: value, isModified: !btn.isNew } : btn
        ));
    };

    const isSaving = updateNodeMutation.isPending ||
        createButtonMutation.isPending ||
        updateButtonMutation.isPending ||
        deleteButtonMutation.isPending ||
        reorderButtonsMutation.isPending;

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Template de Foto"
                            className="mt-1.5"
                            disabled={isSaving}
                        />
                    </div>

                    <div>
                        <Label>Imagem *</Label>
                        {mediaUrl ? (
                            <div className="mt-1.5 space-y-2">
                                {/* 1,91:1 e a proporcao em que a Meta renderiza — o preview
                                    mostra exatamente o que vai aparecer no Messenger. */}
                                <div className="relative aspect-[1.91/1] bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                                    <img
                                        src={versaoLeve(mediaUrl)}
                                        onError={(e) => aoFalharVersaoLeve(e, mediaUrl)}
                                        alt="Preview"
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex items-center justify-between gap-2 p-3 bg-gray-50 rounded-lg">
                                    <span className="text-sm text-gray-700 truncate flex-1">
                                        {mediaFilename}
                                    </span>
                                    {/* Em node antigo nao existe media_source_url, mas o
                                        media_url dele E a arte original — nunca passou por
                                        recorte. Por isso serve de origem, e o botao aparece
                                        tambem nos flows que ja existiam. */}
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setCropSourceUrl(mediaSourceUrl || mediaUrl)}
                                        disabled={isSaving}
                                        title={mediaSourceUrl
                                            ? 'Reabrir o editor no enquadramento salvo'
                                            : 'Enquadrar esta arte em 1,91:1'}
                                    >
                                        <Crop className="w-3.5 h-3.5 mr-1.5" />
                                        Ajustar
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsMediaLibraryOpen(true)}
                                        disabled={isSaving}
                                    >
                                        Trocar
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsMediaLibraryOpen(true)}
                                disabled={isSaving}
                                className="mt-1.5 w-full aspect-[1.91/1] bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-colors flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ImageIcon className="w-8 h-8 text-gray-400" />
                                <span className="text-sm text-gray-600">Clique para selecionar imagem</span>
                            </button>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="title">Título *</Label>
                        <Input
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Digite o título principal"
                            className="mt-1.5"
                            maxLength={80}
                            disabled={isSaving}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {title.length}/80 caracteres
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="subtitle">Subtítulo (opcional)</Label>
                        <Textarea
                            id="subtitle"
                            value={subtitle}
                            onChange={(e) => setSubtitle(e.target.value)}
                            placeholder="Digite o subtítulo (opcional)"
                            className="mt-1.5 min-h-[80px]"
                            maxLength={200}
                            disabled={isSaving}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {subtitle.length}/200 caracteres
                        </p>
                    </div>

                    <div>
                        <Label className="flex items-center gap-1.5 mb-2">
                            <MousePointerClick className="w-3.5 h-3.5" />
                            URL ao clicar na imagem (opcional)
                        </Label>

                        <div className="space-y-3 p-3 bg-gray-50 border rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                    <Shuffle className="w-3.5 h-3.5 text-blue-500" />
                                    <span className="text-xs font-semibold text-gray-600">Configuração de Redirecionamento</span>
                                </div>
                                {mediaClickUrlEntries.length > 0 && (
                                    <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full ${Math.round(mediaClickUrlEntries.reduce((acc, curr) => acc + (curr.weight || 0), 0)) === 100
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-600'
                                        }`}>
                                        {mediaClickUrlEntries.reduce((acc, curr) => acc + (curr.weight || 0), 0)}%
                                    </span>
                                )}
                            </div>

                            {mediaClickUrlEntries.map((entry, idx) => (
                                <div key={idx} className="bg-white border rounded-lg p-2.5 space-y-1.5 relative group shadow-sm">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">URL {idx + 1}</span>
                                        {mediaClickUrlEntries.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newEntries = [...mediaClickUrlEntries];
                                                    newEntries.splice(idx, 1);
                                                    setMediaClickUrlEntries(newEntries);
                                                }}
                                                className="p-1 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                                                disabled={isSaving}
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                    <SmartTextField
                                        value={entry.url}
                                        onChange={(val) => {
                                            const newEntries = [...mediaClickUrlEntries];
                                            newEntries[idx] = { ...newEntries[idx], url: val };
                                            setMediaClickUrlEntries(newEntries);
                                        }}
                                        variables={pageVariables}
                                        placeholder="https://exemplo.com/{{lead_id}}"
                                        singleLine
                                        inputClassName="h-8 text-xs font-medium"
                                    />
                                    <div className="flex items-center gap-2 mt-1 px-1">
                                        <span className="text-[10px] text-gray-500 font-medium">Peso (%)</span>
                                        <Input
                                            type="number"
                                            min={0}
                                            max={100}
                                            placeholder="0"
                                            value={entry.weight === 0 && entry.url === '' ? '' : entry.weight}
                                            disabled={isSaving}
                                            onChange={(e) => {
                                                const newEntries = [...mediaClickUrlEntries];
                                                newEntries[idx] = { ...newEntries[idx], weight: Number(e.target.value) || 0 };
                                                setMediaClickUrlEntries(newEntries);
                                            }}
                                            className="h-7 text-xs w-16 bg-gray-50 border-none shadow-none focus-visible:ring-1"
                                        />
                                    </div>
                                </div>
                            ))}

                            {mediaClickUrlEntries.length > 0 && Math.round(mediaClickUrlEntries.reduce((acc, curr) => acc + (curr.weight || 0), 0)) !== 100 && (
                                <div className="flex items-center gap-1.5 text-[10px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-2 py-1">
                                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                    <span>Soma deve ser 100%.</span>
                                </div>
                            )}

                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setMediaClickUrlEntries([
                                        ...mediaClickUrlEntries,
                                        { url: '', weight: mediaClickUrlEntries.length === 0 ? 100 : 0 }
                                    ]);
                                }}
                                disabled={isSaving || mediaClickUrlEntries.length >= 5}
                                className="w-full h-8 text-[10px] font-semibold border-dashed text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                                <Plus className="w-3 h-3 mr-1" />
                                Adicionar URL Ponderada
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-2.5">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium text-gray-700">
                                Botões
                                {buttons.length > 0 && (
                                    <span className="ml-2 text-xs font-normal text-gray-500">({buttons.length}/3)</span>
                                )}
                            </Label>
                            {buttons.length > 0 && (
                                <span className="text-[10px] text-gray-500">
                                    ↕️ Arraste para reordenar
                                </span>
                            )}
                        </div>

                        {buttons.length === 0 && (
                            <div className="text-center py-4 border-2 border-dashed border-gray-200 rounded-lg">
                                <p className="text-xs text-gray-500 mb-2">Nenhum botão adicionado</p>
                                <Button
                                    type="button"
                                    onClick={handleAddButton}
                                    variant="outline"
                                    size="sm"
                                    className="mx-auto h-8 text-xs"
                                    disabled={isSaving}
                                >
                                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                                    Adicionar Primeiro Botão
                                </Button>
                            </div>
                        )}

                        {buttons.length > 0 && (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={buttons.map(btn => getButtonUniqueId(btn))}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="space-y-1.5">
                                        {buttons.map((button, index) => (
                                            <SortableButton
                                                key={button.tempId}
                                                button={button}
                                                index={index}
                                                onUpdate={handleUpdateButton}
                                                onRemove={handleRemoveButton}
                                                disabled={isSaving}
                                                pageVariables={pageVariables}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        )}

                        {buttons.length > 0 && buttons.length < 3 && (
                            <Button
                                type="button"
                                onClick={handleAddButton}
                                variant="outline"
                                className="w-full h-8 border-dashed border-2 text-xs"
                                disabled={isSaving}
                            >
                                <Plus className="w-3.5 h-3.5 mr-1.5" />
                                Adicionar Botão ({buttons.length}/3)
                            </Button>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" />
                            Digitando... (segundos)
                        </Label>
                        <Input
                            type="number"
                            min={0}
                            max={30}
                            value={typingSeconds}
                            onChange={(e) => setTypingSeconds(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                            placeholder="0"
                            className="h-10 w-32"
                            disabled={isSaving}
                        />
                        <p className="text-xs text-gray-500">
                            Mostra "digitando..." por X segundos antes de enviar. 0 = desativado.
                        </p>
                    </div>

                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                        <p className="text-xs text-cyan-900">
                            💡 <strong>Dica:</strong> Arraste os botões para reordená-los. Botões de "Ação" permitem conectar este node a outros nodes do fluxo.
                        </p>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={isSaving}>
                    Cancelar
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Salvando...' : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>

            <MediaLibraryModal
                isOpen={isMediaLibraryOpen}
                onClose={() => setIsMediaLibraryOpen(false)}
                onSelect={handleMediaSelect}
                pageId={pageId}
                mediaType="image"
            />

            <ImageCropModal
                isOpen={!!cropSourceUrl}
                onClose={() => setCropSourceUrl(null)}
                sourceUrl={cropSourceUrl || ''}
                initialCrop={cropSourceUrl === mediaSourceUrl ? mediaCrop : null}
                onConfirm={handleCropConfirm}
            />
            <ConfirmDialog
                open={confirmRemove !== null}
                onOpenChange={(open) => { if (!open) setConfirmRemove(null); }}
                title={`Excluir o botão "${confirmRemove?.text || 'sem texto'}"?`}
                description="Os cliques deste botão ficam registrados pelo botão. Se você excluir e criar outro no lugar, o novo começa do zero e o histórico do antigo passa a aparecer só como 'Botões excluídos' no desempenho do node. Para trocar o texto ou o link, edite o botão em vez de excluir."
                confirmLabel="Excluir mesmo assim"
                cancelLabel="Vou editar"
                variant="danger"
                onConfirm={() => {
                    const tempId = confirmRemove?.tempId;
                    setConfirmRemove(null);
                    if (tempId) setButtons(prev => prev.filter(b => b.tempId !== tempId));
                }}
            />
        </>
    );
}